import { useState } from 'react';
import { Trash2 } from 'lucide-react';
import { Button, Input, Pill } from '@/shared/components';
import type { SavedSearchSummary } from '@/shared/api';

interface Props {
  items: SavedSearchSummary[];
  isLoading: boolean;
  onLoad: (id: string) => void;
  onDelete: (id: string) => void;
  onSave: (name: string) => void;
  canSave: boolean;
  saving: boolean;
}

export function SavedSearchPanel({
  items,
  isLoading,
  onLoad,
  onDelete,
  onSave,
  canSave,
  saving,
}: Props) {
  const [name, setName] = useState('');

  const submit = () => {
    const trimmed = name.trim();
    if (!trimmed) return;
    onSave(trimmed);
    setName('');
  };

  return (
    <aside className="border-line/50 bg-card w-full space-y-4 rounded-lg border p-4 lg:w-72">
      <div>
        <h2 className="text-ink text-[13px] font-semibold uppercase tracking-wider">Vorlagen</h2>
        <div className="mt-3 space-y-2">
          <Input
            aria-label="Vorlagenname"
            placeholder="Name der Vorlage"
            value={name}
            onChange={(e) => setName(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && submit()}
            disabled={!canSave}
          />
          <Button
            variant="brand"
            size="sm"
            className="w-full"
            disabled={!canSave || !name.trim()}
            loading={saving}
            onClick={submit}
          >
            Aktuelle Suche speichern
          </Button>
        </div>
      </div>

      <div className="space-y-1">
        {isLoading && <p className="text-ink-faint text-xs">Lädt …</p>}
        {!isLoading && items.length === 0 && (
          <p className="text-ink-faint text-xs">Noch keine gespeicherten Suchen.</p>
        )}
        {items.map((item) => (
          <div
            key={item.id}
            className="hover:bg-card-alt group flex items-center gap-1 rounded-md px-2 py-1.5"
          >
            <button
              type="button"
              onClick={() => onLoad(item.id)}
              className="min-w-0 flex-1 text-left"
              title={item.dataset}
            >
              <span className="text-ink block truncate text-sm">{item.name}</span>
              <span className="text-ink-faint text-[11px]">{item.dataset}</span>
            </button>
            {item.isStale && (
              <Pill variant="warning" size="sm">
                veraltet
              </Pill>
            )}
            <Button
              variant="ghost"
              size="icon"
              aria-label="Vorlage löschen"
              className="h-7 w-7 shrink-0 opacity-0 group-hover:opacity-100"
              onClick={() => onDelete(item.id)}
            >
              <Trash2 size={14} strokeWidth={2} />
            </Button>
          </div>
        ))}
      </div>
    </aside>
  );
}
